import { AppointmentTypeGroup } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { i18next } from '@frontend/i18n';
import { BookingCadenceOptions } from '../availability-form/types';

dayjs.extend(customParseFormat);

export const getSelectedServiceDuration = ({
  appointmentTypeGroups,
  selectedServiceIds,
}: {
  appointmentTypeGroups: AppointmentTypeGroup[];
  selectedServiceIds: string[];
}) => {
  const serviceDurationsById = new Map(
    appointmentTypeGroups
      .flatMap((group) => group.bookableAppointmentTypes ?? [])
      .map((service) => [service.id, service.duration])
  );

  const selectedDurations = selectedServiceIds
    .map((serviceId) => serviceDurationsById.get(serviceId))
    .filter((duration): duration is number => typeof duration === 'number' && duration > 0);

  if (selectedDurations.length === 0) return null;

  // Use the longest selected duration
  return Math.max(...selectedDurations);
};

export const convertTimeToMinutes = (time: string | undefined) => {
  if (!time) return null;

  const parsed = dayjs(time, ['HH:mm:ss', 'HH:mm'], true);
  if (!parsed.isValid()) return null;

  return parsed.hour() * 60 + parsed.minute();
};

export const formatMinutesToTimeLabel = (minutesFromMidnight: number) => {
  return dayjs().startOf('day').add(minutesFromMidnight, 'minute').format('h:mm A');
};

export const getCadenceStartTimes = ({
  cadenceId,
  startMinutes,
  endMinutes,
  serviceDuration,
}: {
  cadenceId: BookingCadenceOptions;
  startMinutes: number;
  endMinutes: number;
  serviceDuration: number;
}) => {
  const startTimes: number[] = [];

  if (serviceDuration <= 0) return startTimes;

  if (cadenceId === 'NONE') {
    for (let current = startMinutes; current + serviceDuration <= endMinutes; current += serviceDuration) {
      startTimes.push(current);
    }
    return startTimes;
  }

  const stepMinutes = cadenceId === 'EVERY_HALF_HOUR' ? 30 : 60;
  const alignedStart = cadenceId === 'TOP_OF_THE_HOUR' ? Math.ceil(startMinutes / 60) * 60 : startMinutes;

  for (let current = alignedStart; current + serviceDuration <= endMinutes; current += stepMinutes) {
    startTimes.push(current);
  }

  return startTimes;
};

export const getCadenceSummaryLabel = ({
  cadenceId,
  startMinutes,
  endMinutes,
  serviceDuration,
}: {
  cadenceId: BookingCadenceOptions;
  startMinutes: number;
  endMinutes: number;
  serviceDuration: number;
}) => {
  if (cadenceId === 'NONE') {
    return i18next.t('Continuous booking (no set intervals) Start times fill gaps as previous appointments end.', {
      ns: 'schedule-settings',
    });
  }

  if (cadenceId === 'TOP_OF_THE_HOUR') {
    return i18next.t('Slots at the top of each hour (on the hour)', {
      ns: 'schedule-settings',
    });
  }

  if (cadenceId === 'EVERY_HOUR') {
    return i18next.t('Slots every hour from {{start}} - {{end}}', {
      ns: 'schedule-settings',
      start: formatMinutesToTimeLabel(startMinutes),
      end: formatMinutesToTimeLabel(endMinutes),
    });
  }

  if (cadenceId === 'EVERY_HALF_HOUR') {
    return i18next.t('Slots every 30 minutes from {{start}} - {{end}}', {
      ns: 'schedule-settings',
      start: formatMinutesToTimeLabel(startMinutes),
      end: formatMinutesToTimeLabel(endMinutes),
    });
  }

  return i18next.t('Continuous booking with {{duration}} minute service duration', {
    ns: 'schedule-settings',
    duration: serviceDuration,
  });
};

export const getCadencePreviewDescription = ({
  cadenceId,
  startMinutes,
  endMinutes,
  serviceDuration,
  startTimes,
}: {
  cadenceId: BookingCadenceOptions;
  startMinutes: number;
  endMinutes: number;
  serviceDuration: number;
  startTimes: number[];
}) => {
  const previewStartTimes = startTimes.slice(0, 4).map((minutes) => formatMinutesToTimeLabel(minutes));
  const hasMoreTimes = startTimes.length > previewStartTimes.length;
  const timesText = `${previewStartTimes.join(', ')}${hasMoreTimes ? '...' : ''}`;

  if (cadenceId === 'NONE') {
    return `${getCadenceSummaryLabel({
      cadenceId,
      startMinutes,
      endMinutes,
      serviceDuration,
    }).replace(/[.!?]\s*$/, '')}: ${timesText}`;
  }

  return `${getCadenceSummaryLabel({
    cadenceId,
    startMinutes,
    endMinutes,
    serviceDuration,
  })} ${i18next.t('Available start times: {{times}}', {
    ns: 'schedule-settings',
    times: timesText,
  })}`;
};

export const getCadencePreviewDescriptionForRange = ({
  cadenceId,
  startMinutes,
  endMinutes,
  serviceDuration,
}: {
  cadenceId: BookingCadenceOptions;
  startMinutes: number;
  endMinutes: number;
  serviceDuration: number;
}) => {
  const startTimes = getCadenceStartTimes({
    cadenceId,
    startMinutes,
    endMinutes,
    serviceDuration,
  });

  if (startTimes.length === 0) {
    return i18next.t('No available start times in this range for the selected service duration.', {
      ns: 'schedule-settings',
    });
  }

  return getCadencePreviewDescription({
    cadenceId,
    startMinutes,
    endMinutes,
    serviceDuration,
    startTimes,
  });
};
